import {
  BookmarkToggleButton,
  PromptCard,
  PromptTabs,
  type Filter,
  type Prompt,
} from "@1js/pl-card-components";
import * as React from "react";

import { Column, PromptCardSkeleton, Row } from "@1js/pl-card-components";
import type { Group } from "@1js/pl-types";
import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import type { SearchResultsData } from "../../types";
import { PromptDetailModal } from "../PromptDetail/PromptDetailModal.web";
import type { WebPrompt } from "../data";
import type { HandlePromptsTabSelected } from "./AllPrompts.web.data";
import { useStyles } from "./AllPrompts.web.styles";
import { useBookmarkPrompts } from "./BookmarkPrompts.hook.web";
import { Filters } from "./Filters.web";
import { PromptHeadingV2 } from "./PromptHeadingV2";
import { ShowMore } from "./ShowMore.web";
import { useColumnStyle } from "./useColumnStyle.hook";

export type BookmarkPromptsProps = {
  isLoading: boolean;
  skeletonCount: number;
  promptsToDisplay: Prompt[];
  promptRefs: React.MutableRefObject<
    Map<string, React.RefObject<HTMLDivElement>>
  >;
  currentFocusedPromptId: string | null;
  setCurrentFocusedPromptId: (promptId: string) => void;
  shareToWorkgroup: (prompts: Prompt[]) => void;
  handleShareViaCopyLinkClicked?: (prompt: Prompt) => Promise<void>;
  handleShareViaEmailClicked?: (prompt: Prompt) => Promise<void>;
  handleCardClick: (prompt: Prompt) => void;
  onPromptBookmarkToggled: (prompt: Prompt) => Promise<void>;
  onDelete?: (prompt: Prompt) => void;
  setLastFocusedPromptId: (promptId: string) => void;
  prompts: Prompt[];
  groups?: Group[];
  isShowHasMore: boolean;
  hasMore?: boolean;
  handleCardFocus: () => void;
  onMoreClicked: () => void;
  resultsAnnouncementId: string;
  swipe: {
    setSwipedState: (swiped: boolean) => void;
  };
  isWorkgroupPublishingEnabled: boolean;
  dialogOpen: boolean;
  handleDialogClose: () => void;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  activePrompt: WebPrompt | undefined;
  morePrompts: WebPrompt[];
  onSharePromptClicked: ((prompt: Prompt) => void) | undefined;
  onCopyPromptClicked: ((prompt: Prompt) => void) | undefined;
  onTryInAppBadgeAction:
    | ((
        prompt: Prompt,
        product: string,
        tagRef: React.MutableRefObject<null>
      ) => void)
    | undefined;
  isShowFilters: boolean;
  onBookmarkToggled: (() => void) | undefined;
  filters: Filter[];
  selectedFacets: Record<string, string[]>;
  handleFilterChange: (
    selectedValues: Record<string, string[]>,
    filters: Filter[]
  ) => void;
  children: React.ReactNode[] | React.ReactNode;
  handlePromptsTabSelected: HandlePromptsTabSelected;
  onFiltersCleared: () => void;
  onFiltersOpenChanged: (isOpen: boolean, filters: Filter[]) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  isMetaOsV2?: boolean;
  searchResultsData?: SearchResultsData;
};

export const BookmarkPrompts: React.FC<BookmarkPromptsProps> = ({
  currentFocusedPromptId,
  setCurrentFocusedPromptId,
  isLoading,
  skeletonCount,
  promptsToDisplay,
  promptRefs,
  shareToWorkgroup,
  handleShareViaCopyLinkClicked,
  handleShareViaEmailClicked,
  handleCardClick,
  onPromptBookmarkToggled,
  onDelete,
  setLastFocusedPromptId,
  prompts,
  groups,
  isShowHasMore,
  hasMore,
  handleCardFocus,
  onMoreClicked,
  resultsAnnouncementId,
  isWorkgroupPublishingEnabled,
  dialogOpen,
  handleDialogClose,
  setDialogOpen,
  activePrompt,
  morePrompts,
  onSharePromptClicked,
  onCopyPromptClicked,
  onTryInAppBadgeAction,
  isShowFilters,
  onBookmarkToggled,
  filters,
  selectedFacets,
  handleFilterChange,
  children,
  handlePromptsTabSelected,
  onFiltersCleared,
  onFiltersOpenChanged,
  handleKeyDown,
  isMetaOsV2,
  searchResultsData,
}: BookmarkPromptsProps) => {
  const styles = useStyles();
  const {
    isFlightActive,
    handleOnBookmarkToggled,
    handleOnDelete,
    chooseBestProductForHandoff,
  } = useBookmarkPrompts({
    handlePromptsTabSelected,
    onBookmarkToggled,
    onDelete,
    setDialogOpen,
  });
  const appearance = isFlightActive("isGoBoldPromptCardEnabled")
    ? "web-v2"
    : "web";
  const cardColStyles = useColumnStyle();
  const { state } = React.useContext(LibraryContext);

  return (
    <>
      {isMetaOsV2 && (
        <PromptHeadingV2
          navItem={PromptTabs.SavedPrompts}
          searchResultsData={searchResultsData}
        />
      )}
      {isShowFilters && (
        <div className={styles.filtersRow}>
          {isFlightActive("isBookmarksEnabled") &&
            !isFlightActive("isPromptSavingEnabled") && (
              <BookmarkToggleButton
                className={styles.bookmarkToggle}
                isLoading={isLoading}
                showBookmarkOnly={true}
                onClicked={handleOnBookmarkToggled}
              />
            )}
          <div className={styles.filtersContainer}>
            <Filters
              filters={filters}
              selectedFilters={selectedFacets}
              isLoading={isLoading}
              onFilterChanged={handleFilterChange}
              onCleared={onFiltersCleared}
              onOpenChanged={onFiltersOpenChanged}
              isMetaOsV2={isMetaOsV2}
            />
          </div>
        </div>
      )}
      {children}
      {isLoading ? (
        <Row className={styles.cards}>
          {/* create skeletonCount number of PromptCardSkelton component */}
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <Column key={index} size={3} className={cardColStyles}>
              <PromptCardSkeleton
                appearance={appearance}
                isFavoriteEnabled={isFlightActive("isBookmarksEnabled")}
              />
            </Column>
          ))}
        </Row>
      ) : (
        <>
          <Row
            role="list"
            aria-label="Prompt"
            aria-describedby={resultsAnnouncementId}
            className={styles.cards}
            data-testid="prompt-cards"
            onKeyDown={handleKeyDown}
          >
            {promptsToDisplay.map((prompt: Prompt, idx: number) => {
              if (!promptRefs.current.has(prompt.Id)) {
                promptRefs.current.set(prompt.Id, React.createRef());
              }
              const handleFocus = () => {
                setLastFocusedPromptId(prompt.Id);
                setCurrentFocusedPromptId(prompt.Id);
              };
              return (
                <Column key={prompt.Id} size={3} className={cardColStyles}>
                  <PromptCard
                    appearance={appearance}
                    key={prompt.Id}
                    displayPosition={idx}
                    isPromptFocused={currentFocusedPromptId === prompt.Id}
                    onFocus={handleFocus}
                    prompt={prompt}
                    isFavoriteEnabled={isFlightActive("isBookmarksEnabled")}
                    isAgentPromptFavoriteEnabled={isFlightActive(
                      "isAgentPromptFavoriteEnabled"
                    )}
                    isPromptSavingEnabled={isFlightActive(
                      "isPromptSavingEnabled"
                    )}
                    isPromptSharingEnabled={isFlightActive(
                      "isPromptSharingEnabled"
                    )}
                    isPromptSharingViaEmailEnabled={isFlightActive(
                      "isPromptSharingViaEmailEnabled"
                    )}
                    isPromptSharingToWorkgroupEnabled={
                      isWorkgroupPublishingEnabled &&
                      (isFlightActive(
                        "isWorkgroupPublishingMicrosoftPromptsEnabled"
                      ) ||
                        prompt.Origin === "User")
                    }
                    onShareToWorkgroupClicked={shareToWorkgroup}
                    onShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
                    onShareViaEmailClicked={handleShareViaEmailClicked}
                    onClicked={handleCardClick}
                    onFavoriteToggled={onPromptBookmarkToggled}
                    isBadgesEnabled={true}
                    ref={promptRefs.current.get(prompt.Id)}
                    onDelete={onDelete}
                    aria-posinset={idx + 1}
                    aria-setsize={prompts.length}
                    groups={groups}
                    handoffProduct={chooseBestProductForHandoff(prompt)}
                    extensionIconMap={
                      isFlightActive("isAgentPromptSavingEnabled") ||
                      isFlightActive("isAgentPromptFavoriteEnabled")
                        ? state.extensionIconMap
                        : undefined
                    }
                  />
                </Column>
              );
            })}
          </Row>
          <ShowMore
            isShowHasMore={isShowHasMore}
            hasMore={hasMore}
            isLoading={isLoading}
            handleCardFocus={handleCardFocus}
            onMoreClicked={onMoreClicked}
          />
          {activePrompt ? (
            <PromptDetailModal
              dialogOpen={dialogOpen}
              setDialogOpen={setDialogOpen}
              handleDialogClose={handleDialogClose}
              prompt={activePrompt as WebPrompt}
              morePrompts={morePrompts}
              onMoreLikeThisClicked={handleCardClick}
              onBookmarkToggled={onPromptBookmarkToggled}
              onShareClicked={onSharePromptClicked}
              onCopyClicked={onCopyPromptClicked}
              isBookmarkEnabled={isFlightActive("isBookmarksEnabled")}
              isAgentPromptSavingEnabled={isFlightActive(
                "isAgentPromptSavingEnabled"
              )}
              isPromptSavingEnabled={isFlightActive("isPromptSavingEnabled")}
              isPromptSharingEnabled={isFlightActive("isPromptSharingEnabled")}
              isPromptSharingViaEmailEnabled={isFlightActive(
                "isPromptSharingViaEmailEnabled"
              )}
              isPromptSharingToWorkgroupEnabled={
                isWorkgroupPublishingEnabled &&
                (isFlightActive(
                  "isWorkgroupPublishingMicrosoftPromptsEnabled"
                ) ||
                  activePrompt.Origin === "User")
              }
              groups={groups}
              onShareToWorkgroupClicked={shareToWorkgroup}
              onDelete={handleOnDelete}
              isPromptCopyLinkEnabled={true}
              onTryInAppBadgeAction={onTryInAppBadgeAction || undefined}
            />
          ) : (
            <></>
          )}
        </>
      )}
    </>
  );
};
